import { useState, useEffect, useRef } from "react";

const SKILLS = [
  { name: "Next.js", pct: 95 },
  { name: "TypeScript", pct: 90 },
  { name: "React", pct: 95 },
  { name: "Supabase", pct: 85 },
  { name: "Tailwind CSS", pct: 92 },
  { name: "Gemini AI", pct: 82 },
  { name: "Node.js", pct: 88 },
  { name: "PostgreSQL", pct: 80 },
];

const PROJECTS = [
  {
    num: "01",
    title: "JusticeLaw Platform",
    category: "Legal Tech · AI",
    desc: "Full-scale AI-powered legal platform — attorney matching, document triage, intelligent intake, and a consumer chatbot built on Gemini API.",
    tags: ["Next.js", "Gemini AI", "Supabase", "TypeScript"],
    emoji: "⚖️",
    color: "#7c3aed",
  },
  {
    num: "02",
    title: "AI Automation Layer",
    category: "AI System · API",
    desc: "7-module intelligent automation: triage engine, FAQ generation, document categorization, intake assistant — unified through one smart chat interface.",
    tags: ["Gemini REST", "Next.js", "TypeScript"],
    emoji: "🤖",
    color: "#0891b2",
  },
  {
    num: "03",
    title: "Multan IT Directory",
    category: "Research · Data",
    desc: "80+ company directory of software houses across Multan's tech ecosystem — geo-coded, color-classified, and built for the local developer community.",
    tags: ["Research", "Excel", "Data Analysis"],
    emoji: "🗂️",
    color: "#059669",
  },
];

const WORDS = ["Next.js Architect.", "AI Integration.", "Legal Tech Builder.", "Fullstack Engineer."];

function useInView(ref, threshold = 0.13) {
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold });
    o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return v;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isMobile;
}

function Typer({ words }) {
  const [idx, setIdx] = useState(0);
  const [txt, setTxt] = useState("");
  const [del, setDel] = useState(false);
  useEffect(() => {
    const w = words[idx % words.length];
    const t = setTimeout(() => {
      if (!del && txt === w) return setDel(true);
      if (del && txt === "") { setDel(false); return setIdx(i => i + 1); }
      setTxt(del ? txt.slice(0, -1) : w.slice(0, txt.length + 1));
    }, del ? 40 : 80);
    return () => clearTimeout(t);
  }, [txt, del, idx]);
  return <span>{txt}<span style={{ animation: "blink 1s steps(1) infinite" }}>|</span></span>;
}

function FadeUp({ children, delay = 0, style = {} }) {
  const ref = useRef();
  const v = useInView(ref);
  return (
    <div ref={ref} style={{
      opacity: v ? 1 : 0,
      transform: v ? "translateY(0)" : "translateY(32px)",
      transition: `opacity 0.8s cubic-bezier(.4,0,.2,1) ${delay}s, transform 0.8s cubic-bezier(.4,0,.2,1) ${delay}s`,
      ...style,
    }}>{children}</div>
  );
}

function SkillBar({ name, pct, delay }) {
  const ref = useRef();
  const v = useInView(ref);
  return (
    <div ref={ref} style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{name}</span>
        <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>{pct}%</span>
      </div>
      <div style={{ height: 5, background: "#e2e8f0", borderRadius: 99, overflow: "hidden" }}>
        <div style={{
          height: "100%", borderRadius: 99,
          background: "linear-gradient(90deg, #7c3aed, #0891b2)",
          width: v ? `${pct}%` : "0%",
          transition: `width 1.3s cubic-bezier(.4,0,.2,1) ${delay}ms`,
        }} />
      </div>
    </div>
  );
}

function ProjectCard({ p, i }) {
  const ref = useRef();
  const v = useInView(ref);
  const [hov, setHov] = useState(false);
  return (
    <div ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: "#fff",
        borderRadius: 20,
        padding: "28px 24px 24px",
        border: `1.5px solid ${hov ? p.color + "44" : "#f1f5f9"}`,
        boxShadow: hov ? `0 20px 60px ${p.color}14, 0 4px 24px rgba(0,0,0,0.06)` : "0 2px 16px rgba(0,0,0,0.04)",
        transform: v ? (hov ? "translateY(-6px)" : "translateY(0)") : "translateY(40px)",
        opacity: v ? 1 : 0,
        transition: `all 0.5s cubic-bezier(.4,0,.2,1) ${i * 0.1}s`,
        cursor: "default",
        position: "relative",
        overflow: "hidden",
      }}>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${p.color}, ${p.color}88)`,
        opacity: hov ? 1 : 0,
        transition: "opacity 0.4s",
        borderRadius: "20px 20px 0 0",
      }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <span style={{ fontSize: 36 }}>{p.emoji}</span>
        <span style={{ fontSize: 36, fontWeight: 900, color: hov ? p.color : "#f1f5f9", transition: "color 0.3s", lineHeight: 1, letterSpacing: -2 }}>{p.num}</span>
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: p.color, marginBottom: 8 }}>{p.category}</div>
      <h3 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: "0 0 10px", letterSpacing: -0.5 }}>{p.title}</h3>
      <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.75, margin: "0 0 20px" }}>{p.desc}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {p.tags.map(t => (
          <span key={t} style={{ fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 99, background: `${p.color}0f`, color: p.color, border: `1px solid ${p.color}22`, letterSpacing: 0.3 }}>{t}</span>
        ))}
      </div>
    </div>
  );
}

function Stat({ num, label, i }) {
  const ref = useRef();
  const v = useInView(ref);
  return (
    <div ref={ref} style={{
      textAlign: "center", padding: "24px 12px",
      background: "#fff", borderRadius: 16,
      border: "1.5px solid #f1f5f9",
      boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
      opacity: v ? 1 : 0, transform: v ? "none" : "translateY(24px)",
      transition: `all 0.7s cubic-bezier(.4,0,.2,1) ${i * 0.1}s`,
    }}>
      <div style={{ fontSize: 30, fontWeight: 900, background: "linear-gradient(135deg,#7c3aed,#0891b2)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", letterSpacing: -1 }}>{num}</div>
      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4, fontWeight: 500, textTransform: "uppercase", letterSpacing: 1.5 }}>{label}</div>
    </div>
  );
}

export default function Portfolio() {
  const [scrollY, setScrollY] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const scroll = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif", background: "#f8fafc", color: "#0f172a", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes pulse-green { 0%,100%{box-shadow:0 0 0 0 rgba(16,185,129,0.4)} 60%{box-shadow:0 0 0 8px rgba(16,185,129,0)} }
        @keyframes slideDown { from{opacity:0;transform:translateY(-10px)} to{opacity:1;transform:translateY(0)} }
        * { box-sizing:border-box; margin:0; padding:0; }
        html { scroll-behavior:smooth; }
        ::-webkit-scrollbar { width:4px; }
        ::-webkit-scrollbar-thumb { background:linear-gradient(#7c3aed,#0891b2); border-radius:4px; }
        ::selection { background:#7c3aed22; color:#7c3aed; }
      `}</style>

      {/* ── NAV ── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        height: 66, padding: "0 6%",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: scrollY > 40 || menuOpen ? "rgba(248,250,252,0.97)" : "rgba(248,250,252,0)",
        backdropFilter: scrollY > 40 || menuOpen ? "blur(20px)" : "none",
        borderBottom: scrollY > 40 || menuOpen ? "1px solid #e2e8f0" : "none",
        transition: "all 0.4s ease",
      }}>
        {/* Logo */}
        <div style={{ fontWeight: 900, fontSize: 21, letterSpacing: -0.5, background: "linear-gradient(135deg,#7c3aed,#0891b2)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          HaSan.
        </div>

        {/* Desktop nav links */}
        {!isMobile && (
          <div style={{ display: "flex", gap: 8 }}>
            {["Home", "About", "Skills", "Work", "Contact"].map(n => (
              <button key={n} onClick={() => scroll(n)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 500, color: "#64748b", padding: "8px 14px", borderRadius: 8, transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.color = "#0f172a"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#64748b"; }}
              >{n}</button>
            ))}
          </div>
        )}

        {/* Desktop status / Mobile hamburger */}
        {isMobile ? (
          <button onClick={() => setMenuOpen(o => !o)} style={{ background: "none", border: "none", cursor: "pointer", padding: 8, display: "flex", flexDirection: "column", gap: 5, justifyContent: "center" }}>
            <span style={{ display: "block", width: 22, height: 2, background: menuOpen ? "#7c3aed" : "#64748b", borderRadius: 2, transition: "all 0.3s", transform: menuOpen ? "rotate(45deg) translate(5px,5px)" : "none" }} />
            <span style={{ display: "block", width: 22, height: 2, background: menuOpen ? "#7c3aed" : "#64748b", borderRadius: 2, transition: "all 0.3s", opacity: menuOpen ? 0 : 1 }} />
            <span style={{ display: "block", width: 22, height: 2, background: menuOpen ? "#7c3aed" : "#64748b", borderRadius: 2, transition: "all 0.3s", transform: menuOpen ? "rotate(-45deg) translate(5px,-5px)" : "none" }} />
          </button>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#64748b", fontWeight: 500 }}>
            <span style={{ width: 8, height: 8, background: "#10b981", borderRadius: "50%", display: "inline-block", animation: "pulse-green 2s infinite" }} />
            Open to work
          </div>
        )}
      </nav>

      {/* ── MOBILE MENU DROPDOWN ── */}
      {isMobile && menuOpen && (
        <div style={{
          position: "fixed", top: 66, left: 0, right: 0, zIndex: 99,
          background: "rgba(248,250,252,0.97)", backdropFilter: "blur(20px)",
          borderBottom: "1px solid #e2e8f0", padding: "16px 6% 20px",
          animation: "slideDown 0.25s cubic-bezier(.4,0,.2,1)",
        }}>
          {["Home", "About", "Skills", "Work", "Contact"].map(n => (
            <button key={n} onClick={() => scroll(n)} style={{
              display: "block", width: "100%", textAlign: "left",
              background: "none", border: "none", cursor: "pointer",
              fontSize: 16, fontWeight: 500, color: "#374151",
              padding: "13px 4px", borderBottom: "1px solid #f1f5f9",
              transition: "color 0.2s",
            }}
              onMouseEnter={e => { e.currentTarget.style.color = "#7c3aed"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "#374151"; }}
            >{n}</button>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#64748b", fontWeight: 500, marginTop: 14 }}>
            <span style={{ width: 8, height: 8, background: "#10b981", borderRadius: "50%", display: "inline-block", animation: "pulse-green 2s infinite" }} />
            Open to work
          </div>
        </div>
      )}

      {/* ── HERO ── */}
      <section id="Home" style={{ minHeight: "100vh", display: "flex", alignItems: "center", padding: isMobile ? "90px 6% 60px" : "100px 6% 80px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, #7c3aed0a 0%, transparent 70%)", top: "-10%", right: "-5%", pointerEvents: "none" }} />
        <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, #0891b20a 0%, transparent 70%)", bottom: "-5%", left: "-5%", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.1fr 0.9fr", gap: isMobile ? 40 : 60, alignItems: "center" }}>
          {/* Left */}
          <div style={{ animation: "fadeIn 0.9s cubic-bezier(.4,0,.2,1) both" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 99, padding: "6px 16px", marginBottom: 24, fontSize: 13, color: "#64748b", fontWeight: 500 }}>
              <span style={{ width: 7, height: 7, background: "#10b981", borderRadius: "50%", animation: "pulse-green 2s infinite" }} />
              Available for new projects
            </div>

            <h1 style={{ fontSize: isMobile ? "clamp(38px,11vw,56px)" : "clamp(42px,6vw,72px)", fontWeight: 900, lineHeight: 1.06, letterSpacing: -2.5, marginBottom: 16, color: "#0f172a" }}>
              Hi, I'm<br />
              <span style={{ background: "linear-gradient(135deg,#7c3aed 0%,#0891b2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>HaSan</span>
            </h1>

            <div style={{ fontSize: isMobile ? "clamp(16px,4.5vw,22px)" : "clamp(18px,2.2vw,26px)", fontWeight: 300, color: "#64748b", marginBottom: 20, letterSpacing: -0.3, minHeight: "1.5em" }}>
              <Typer words={WORDS} />
            </div>

            <p style={{ fontSize: isMobile ? 15 : 17, color: "#64748b", lineHeight: 1.85, marginBottom: 36, maxWidth: 500 }}>
              Fullstack developer from <strong style={{ color: "#0f172a", fontWeight: 600 }}>Lahore, Pakistan</strong> — turning ideas into fast, elegant, production-grade web platforms.
            </p>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button onClick={() => scroll("Work")} style={{
                background: "linear-gradient(135deg,#7c3aed,#4f46e5)", color: "#fff",
                border: "none", padding: isMobile ? "13px 28px" : "14px 32px", borderRadius: 12,
                fontSize: isMobile ? 14 : 15, fontWeight: 700, cursor: "pointer",
                boxShadow: "0 4px 24px rgba(124,58,237,0.3)", transition: "all 0.25s",
                letterSpacing: 0.2, flex: isMobile ? "1" : "none",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(124,58,237,0.45)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 24px rgba(124,58,237,0.3)"; }}
              >View My Work →</button>
              <button onClick={() => scroll("Contact")} style={{
                background: "#fff", color: "#374151",
                border: "1.5px solid #e2e8f0", padding: isMobile ? "13px 28px" : "14px 32px", borderRadius: 12,
                fontSize: isMobile ? 14 : 15, fontWeight: 600, cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)", transition: "all 0.25s",
                flex: isMobile ? "1" : "none",
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#7c3aed44"; e.currentTarget.style.color = "#7c3aed"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = "#374151"; e.currentTarget.style.transform = "none"; }}
              >Let's Talk</button>
            </div>
          </div>

          {/* Right — profile card */}
          <div style={{ animation: "fadeIn 0.9s 0.2s cubic-bezier(.4,0,.2,1) both" }}>
            <div style={{
              background: "#fff", borderRadius: 28,
              border: "1.5px solid #f1f5f9",
              boxShadow: "0 8px 48px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
              padding: isMobile ? "28px 24px" : "40px 36px", textAlign: "center",
            }}>
              <div style={{ position: "relative", display: "inline-block", marginBottom: 16 }}>
                <div style={{ width: isMobile ? 80 : 100, height: isMobile ? 80 : 100, borderRadius: "50%", background: "linear-gradient(135deg,#ede9fe,#dbeafe)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: isMobile ? 38 : 48, animation: "float 4s ease-in-out infinite", border: "3px solid #fff", boxShadow: "0 4px 24px rgba(124,58,237,0.15)" }}>
                  👨‍💻
                </div>
                <div style={{ position: "absolute", bottom: 4, right: 4, width: 18, height: 18, background: "#10b981", borderRadius: "50%", border: "3px solid #fff" }} />
              </div>
              <div style={{ fontSize: isMobile ? 20 : 22, fontWeight: 800, color: "#0f172a", letterSpacing: -0.5, marginBottom: 4 }}>HaSan</div>
              <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 20, fontWeight: 500 }}>Fullstack Developer · Lahore 🇵🇰</div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
                {[["3+", "Years"], ["15+", "Projects"], ["5+", "Clients"]].map(([n, l]) => (
                  <div key={l} style={{ background: "#f8fafc", borderRadius: 12, padding: isMobile ? "10px 6px" : "12px 8px", border: "1px solid #f1f5f9" }}>
                    <div style={{ fontSize: isMobile ? 17 : 20, fontWeight: 900, color: "#0f172a", letterSpacing: -0.5 }}>{n}</div>
                    <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>{l}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
                {["Next.js", "TypeScript", "Supabase", "Gemini AI", "Tailwind"].map(t => (
                  <span key={t} style={{ fontSize: 11, fontWeight: 600, padding: "5px 12px", borderRadius: 99, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0" }}>{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="About" style={{ padding: isMobile ? "72px 6%" : "110px 6%", background: "#fff", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeUp>
            <div style={{ textAlign: "center", marginBottom: isMobile ? 48 : 72 }}>
              <div style={{ display: "inline-block", fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: "#7c3aed", background: "#f5f3ff", border: "1px solid #ede9fe", borderRadius: 99, padding: "6px 18px", marginBottom: 16 }}>About Me</div>
              <h2 style={{ fontSize: "clamp(26px,6vw,52px)", fontWeight: 900, letterSpacing: -1.5, color: "#0f172a", lineHeight: 1.1 }}>
                Building the web,<br />
                <span style={{ background: "linear-gradient(135deg,#7c3aed,#0891b2)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>one project at a time.</span>
              </h2>
            </div>
          </FadeUp>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 40 : 80, alignItems: "start" }}>
            <FadeUp delay={0.1}>
              <p style={{ fontSize: isMobile ? 15 : 17, color: "#374151", lineHeight: 1.9, marginBottom: 20 }}>
                I'm a <strong style={{ color: "#7c3aed" }}>fullstack developer</strong> from Lahore, Pakistan — building ambitious web applications for clients who care about quality, speed, and elegance.
              </p>
              <p style={{ fontSize: isMobile ? 14 : 16, color: "#64748b", lineHeight: 1.9, marginBottom: 20 }}>
                Currently I'm the lead developer on a large-scale <strong style={{ color: "#0891b2" }}>legal tech platform</strong> — architecting an AI automation layer using Gemini API, complex monetization infrastructure, and legal forms systems.
              </p>
              <p style={{ fontSize: isMobile ? 14 : 16, color: "#64748b", lineHeight: 1.9 }}>
                When I'm not writing code, I'm exploring the local tech scene in Multan & Lahore, drinking chai ☕, and learning the latest in AI.
              </p>
            </FadeUp>

            <FadeUp delay={0.2}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[
                  { icon: "🚀", label: "Currently", value: "Building JusticeLaw — AI legal platform" },
                  { icon: "📍", label: "Location", value: "Lahore, Pakistan 🇵🇰" },
                  { icon: "⚡", label: "Primary Stack", value: "Next.js · TypeScript · Supabase" },
                  { icon: "🤖", label: "AI Integration", value: "Gemini API · REST · Prompt Engineering" },
                  { icon: "💬", label: "Languages", value: "Urdu · Punjabi · English" },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: 14, alignItems: "center", padding: "14px 18px", background: "#f8fafc", borderRadius: 14, border: "1px solid #f1f5f9", transition: "all 0.2s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#ede9fe"; e.currentTarget.style.background = "#fdf4ff"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#f1f5f9"; e.currentTarget.style.background = "#f8fafc"; }}
                  >
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
                    <div>
                      <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 1 }}>{item.label}</div>
                      <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 500 }}>{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ── SKILLS ── */}
      <section id="Skills" style={{ padding: isMobile ? "72px 6%" : "110px 6%", background: "#f8fafc" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeUp>
            <div style={{ textAlign: "center", marginBottom: isMobile ? 40 : 64 }}>
              <div style={{ display: "inline-block", fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: "#0891b2", background: "#ecfeff", border: "1px solid #cffafe", borderRadius: 99, padding: "6px 18px", marginBottom: 16 }}>Tech Stack</div>
              <h2 style={{ fontSize: "clamp(26px,6vw,48px)", fontWeight: 900, letterSpacing: -1.5, color: "#0f172a" }}>Skills & Technologies</h2>
            </div>
          </FadeUp>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? "0" : "0 80px" }}>
            {SKILLS.map((s, i) => <SkillBar key={s.name} name={s.name} pct={s.pct} delay={i * 80} />)}
          </div>
        </div>
      </section>

      {/* ── WORK ── */}
      <section id="Work" style={{ padding: isMobile ? "72px 6%" : "110px 6%", background: "#fff", borderTop: "1px solid #f1f5f9" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeUp>
            <div style={{ textAlign: "center", marginBottom: isMobile ? 40 : 64 }}>
              <div style={{ display: "inline-block", fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: "#059669", background: "#ecfdf5", border: "1px solid #d1fae5", borderRadius: 99, padding: "6px 18px", marginBottom: 16 }}>Portfolio</div>
              <h2 style={{ fontSize: "clamp(26px,6vw,48px)", fontWeight: 900, letterSpacing: -1.5, color: "#0f172a" }}>Featured Projects</h2>
            </div>
          </FadeUp>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit,minmax(300px,1fr))", gap: 20 }}>
            {PROJECTS.map((p, i) => <ProjectCard key={p.num} p={p} i={i} />)}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section style={{ padding: isMobile ? "60px 6%" : "80px 6%", background: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: 14 }}>
          {[["3+", "Years Exp"], ["15+", "Projects"], ["5+", "Clients"], ["100%", "Committed"]].map(([n, l], i) => <Stat key={l} num={n} label={l} i={i} />)}
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="Contact" style={{ padding: isMobile ? "72px 6% 64px" : "110px 6% 100px", background: "#fff", borderTop: "1px solid #f1f5f9", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,#7c3aed08,transparent 70%)", top: "-10%", right: "-5%", pointerEvents: "none" }} />
        <div style={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle,#0891b208,transparent 70%)", bottom: "-10%", left: "5%", pointerEvents: "none" }} />

        <FadeUp style={{ maxWidth: 640, margin: "0 auto", textAlign: "center", position: "relative" }}>
          <div style={{ display: "inline-block", fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: "#7c3aed", background: "#f5f3ff", border: "1px solid #ede9fe", borderRadius: 99, padding: "6px 18px", marginBottom: 20 }}>Contact</div>
          <h2 style={{ fontSize: "clamp(28px,7vw,60px)", fontWeight: 900, letterSpacing: -2, color: "#0f172a", lineHeight: 1.05, marginBottom: 18 }}>
            Let's build something<br />
            <span style={{ background: "linear-gradient(135deg,#7c3aed,#0891b2)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>extraordinary.</span>
          </h2>
          <p style={{ fontSize: isMobile ? 15 : 17, color: "#64748b", lineHeight: 1.8, marginBottom: 36 }}>
            Have a project in mind? I'd love to hear about it. Let's talk about what we can build together.
          </p>

          <a href="mailto:hassankhan1250@gmail.com" style={{
            display: "block", textDecoration: "none",
            background: "linear-gradient(135deg,#7c3aed,#4f46e5)", color: "#fff",
            padding: isMobile ? "15px 36px" : "16px 44px", borderRadius: 14, fontSize: isMobile ? 15 : 16, fontWeight: 700,
            boxShadow: "0 4px 24px rgba(124,58,237,0.3)", transition: "all 0.3s",
            letterSpacing: 0.2, marginBottom: 24,
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 12px 40px rgba(124,58,237,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 24px rgba(124,58,237,0.3)"; }}
          >Send Me a Message ✉</a>

          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            {["GitHub", "LinkedIn", "Twitter", "WhatsApp"].map(s => (
              <button key={s} style={{ background: "#f8fafc", border: "1.5px solid #e2e8f0", color: "#475569", padding: isMobile ? "10px 18px" : "10px 22px", borderRadius: 10, fontSize: 13, cursor: "pointer", fontWeight: 600, transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#7c3aed44"; e.currentTarget.style.color = "#7c3aed"; e.currentTarget.style.background = "#f5f3ff"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = "#475569"; e.currentTarget.style.background = "#f8fafc"; }}
              >{s}</button>
            ))}
          </div>
        </FadeUp>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: "#f8fafc", borderTop: "1px solid #f1f5f9", padding: "24px 6%", display: "flex", justifyContent: isMobile ? "center" : "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, flexDirection: isMobile ? "column" : "row", textAlign: isMobile ? "center" : "left" }}>
        <span style={{ fontWeight: 900, fontSize: 18, background: "linear-gradient(135deg,#7c3aed,#0891b2)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>HaSan.</span>
        <span style={{ color: "#94a3b8", fontSize: 12 }}>Designed & built with ❤ · Lahore, Pakistan 🇵🇰 · {new Date().getFullYear()}</span>
        <span style={{ color: "#cbd5e1", fontSize: 11, fontFamily: "monospace" }}>Next.js · React · TypeScript</span>
      </footer>
    </div>
  );
}
